import { Box, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import XTextField from './XTextField';

export enum ChainIds {
  Ethereum = 1,
  Bsc = 56,
  BscTest = 97,
  Avalanche = 43114,
  FantomOpera = 250,
  Optimism = 10,
  Arbitrum = 42161,
  Polygon = 137,
  Pulse = 369,
  Bitrock = 7171,
  Shibarium = 109,
  Cybria = 6661,
  Base = 8453,
  Solana = 501424,
  BTC = 1800,
  TON = 1100,
  TRX = 100,
  HyperEVM = 999,
  Hyperliquid = 1337,
  Mon = 143,
  Lighter = 3586256,
  Monad = 143,
  Empty = -1,
}

const tokenMap: Record<number, Record<string, string>> = {
  [ChainIds.Bsc]: {
    '0x0000000000000000000000000000000000000000': 'BNB',
    '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 'BNB',
  },
  // Monad Chain
  [ChainIds.Monad]: {
    '0x0000000000000000000000000000000000000000': 'MON',
  },
  // Ethereum Chain
  [ChainIds.Ethereum]: {
    '0x0000000000000000000000000000000000000000': 'ETH',
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2': 'ETH',
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC'
  },
  // Arbitrum Chain
  [ChainIds.Arbitrum]: {
    '0x0000000000000000000000000000000000000000': 'ETH',
    '0x82af49447d8a07e3bd95bd0d56f35241523fbab1': 'ETH', // ARB ETH
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC',
  },
  // HyperEVM Chain
  [ChainIds.HyperEVM]: {
    '0x82af49447d8a07e3bd95bd0d56f35241523fbab1': 'ETH',
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC',
  },
  // Solana Chain
  [ChainIds.Solana]: {
    'so11111111111111111111111111111111111111112': 'SOL', // Chuẩn format địa chỉ Lowercase
    'so11111111111111111111111111111111111111111': 'SOL', // Địa chỉ gốc 
  },
  // HyperLiquid Chain
  [ChainIds.Hyperliquid]: {
    '0x0000000000000000000000000000000000000000': 'USDC',
  },
  // Polygon Chain
  [ChainIds.Polygon]: {
    '0x2791bca1f2de4661ed88a30c99a7a9449aa84174': 'USDC', // Chú ý đã lowerCase address
  },
  [ChainIds.Empty]: {
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC',
  }
}

const tokenIconMap: Record<string, string> = {
  'BNB': '/icons/tokens/bnb.png',
  'ETH': '/icons/tokens/eth.png',
  'SOL': '/icons/tokens/sol.png',
  'USDC': '/icons/tokens/usdc.png',
  'MON': '/icons/tokens/mon.png',
};

const chainAsset: Record<number, string> = {
  [ChainIds.Bsc]: 'smartchain',
  [ChainIds.Ethereum]: 'ethereum',
  [ChainIds.Optimism]: 'optimism',
  [ChainIds.Arbitrum]: 'arbitrum',
  [ChainIds.Polygon]: 'polygon',
  [ChainIds.Pulse]: 'pulse',
  [ChainIds.Bitrock]: 'bitrock',
  [ChainIds.Shibarium]: 'shibarium',
  [ChainIds.Cybria]: 'cybria',
  [ChainIds.Solana]: 'solana',
  [ChainIds.Base]: 'base',
  [ChainIds.Avalanche]: 'avalanchec',
  [ChainIds.BscTest]: 'binance',
  [ChainIds.FantomOpera]: 'fantom',
  [ChainIds.BTC]: 'bitcoin',
  [ChainIds.TON]: 'ton',
  [ChainIds.TRX]: 'tron',
  [ChainIds.Monad]: 'monad',
};

interface XTokenProps {
  chainId?: number | string;
  tokenAddress?: string;
  size?: number;
  showAddress?: boolean;
}

const XToken = ({ chainId, tokenAddress, size = 20, showAddress = false }: XTokenProps) => {
  const [imgError, setImgError] = useState(false);

  if (!tokenAddress) {
    return <Typography>-</Typography>;
  }

  const parsedChainId = typeof chainId === 'string' ? parseInt(chainId, 10) : (chainId || ChainIds.Empty);
  const assetName = chainAsset[parsedChainId] || '';

  const addressLower = tokenAddress.toLowerCase();
  const tokenSymbol = tokenMap[parsedChainId]?.[addressLower];
  const imgUrl = tokenSymbol ? tokenIconMap[tokenSymbol] : '';
  
  const trustWalletSrc = assetName 
    ? `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${assetName}/assets/${tokenAddress}/logo.png`
    : '';

  const handleImgError = () => {
    setImgError(true);
  };

  const finalImgSrc = imgUrl || trustWalletSrc;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {!imgError && finalImgSrc ? (
        <Tooltip title={tokenAddress || ''}>
          <Box
            component="img"
            src={finalImgSrc} // Primarily use local mapping, then trustwallet
            alt="token icon"
            onError={handleImgError}
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip title={tokenAddress || ''}>
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'var(--palette-background-neutral, #e0e0e0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: size * 0.4,
              fontWeight: 'bold',
              color: 'text.secondary',
            }}
          >
            {tokenAddress?.substring(0, 2) || '?'}
          </Box>
        </Tooltip>
      )}
      {showAddress && <XTextField text={tokenAddress} short />}
    </Box>
  );
};

export default XToken;
